// src/components/Plans.js
import React from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaUser, FaFileAlt, FaHandshake, FaShieldAlt, FaClock, FaStethoscope, FaBrain } from 'react-icons/fa';

const Plans = () => {
  // Animaciones
  const cardVariants = {
    offscreen: {
      y: 50,
      opacity: 0,
    },
    onscreen: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        bounce: 0.4,
        duration: 0.8,
      },
    },
  };

  return (
    <div className="min-h-screen py-16">
      <div className="container mx-auto px-4">
        <h1 className="text-5xl font-bold text-center mb-6 text-gray-800">
          Nuestros Planes de Selección
        </h1>
        <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">
          Cada plan está diseñado para ofrecerte diferentes niveles de seguridad y tranquilidad en la selección de tu niñera. Elige el que mejor se adapte a tus necesidades familiares.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Plan África */}
          <motion.div
            initial="offscreen"
            whileInView="onscreen"
            viewport={{ once: true, amount: 0.5 }}
            variants={cardVariants}
            className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300"
          >
            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold w-fit mb-4">Básico</div>
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Plan África</h2>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <FaFileAlt className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Revisión minuciosa de CV con verificación de experiencia y referencias</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaUser className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Presentación de propuesta laboral a candidatas</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaHandshake className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Entrevistas presenciales a cargo de la familia</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaClock className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Garantía de 10 días</span>
              </li>
            </ul>
          </motion.div>

          {/* Plan Mundial */}
          <motion.div
            initial="offscreen"
            whileInView="onscreen"
            viewport={{ once: true, amount: 0.5 }}
            variants={cardVariants}
            className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300"
          >
            <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold w-fit mb-4">Popular</div>
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Plan Mundial</h2>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <FaFileAlt className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Revisión detallada de CV con referencias verificadas</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaUser className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Contacto directo con postulantes</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Entrevista personalizada y preselección de 3-4 perfiles</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaShieldAlt className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Certificado de antecedentes penales incluido</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaClock className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Garantía de 1 mes</span>
              </li>
            </ul>
          </motion.div>

          {/* Plan Mundial XL */}
          <motion.div
            initial="offscreen"
            whileInView="onscreen"
            viewport={{ once: true, amount: 0.5 }}
            variants={cardVariants}
            className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300 relative"
          >
            <div className="absolute top-0 right-0 bg-yellow-500 text-white px-4 py-1 rounded-tr-2xl rounded-bl-2xl text-sm font-bold">Recomendado</div>
            <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold w-fit mb-4">Completo</div>
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Plan Mundial XL</h2>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <FaCheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Todos los beneficios del Plan Mundial</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaStethoscope className="w-5 h-5 text-purple-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Certificado médico emitido por nuestra doctora</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaClock className="w-5 h-5 text-purple-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Garantía extendida a 45 días</span>
              </li>
            </ul>
          </motion.div>

          {/* Plan Terraqueal */}
          <motion.div
            initial="offscreen"
            whileInView="onscreen"
            viewport={{ once: true, amount: 0.5 }}
            variants={cardVariants}
            className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300"
          >
            <div className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold w-fit mb-4">Premium</div>
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Plan Terraqueal</h2>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <FaCheckCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Todos los beneficios del Plan Mundial XL</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaUser className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Reclutamiento y preselección de candidatas</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaFileAlt className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">3 certificados: médico, psicológico y antecedentes penales</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaBrain className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Equipo propio de doctora y psicólogas</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaHandshake className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Llamado a referencias verificadas</span>
              </li>
              <li className="flex items-start space-x-3">
                <FaClock className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Garantía extendida de 3 meses</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Plans;